import React, { useState, useEffect } from 'react';
import { User } from 'lucide-react';
import { RawSpeaker, SessionizeResponse, ProcessedSession, ProcessedSpeaker } from './types';
import './App.css';

// Replace with your actual Sessionize API Endpoint ID
const SESSIONIZE_ID = "w24b5u6e"; 
const API_URL = `https://sessionize.com/api/v2/${SESSIONIZE_ID}/view/All`;

interface TimeIncrement {
  label: string;
  endLabel: string;
  gridRowIndex: number;
}

function App() {
  const [sessions, setSessions] = useState<ProcessedSession[]>([]);
  const [tracks, setTracks] = useState<string[]>([]);
  const [timeLabels, setTimeLabels] = useState<TimeIncrement[]>([]);
  const [selectedSession, setSelectedSession] = useState<ProcessedSession | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetch(API_URL)
      .then((res) => {
        if (!res.ok) throw new Error('Could not pull schedule metadata.');
        return res.json() as Promise<SessionizeResponse>;
      })
      .then((json) => {
        const speakerFormatter = (raw: RawSpeaker) => {
          const fullName = raw.fullName || `${raw.firstName} ${raw.lastName}`.trim() || raw.name || 'TBA';
          let profilePicture = raw.profilePicture || null;

          if (profilePicture && profilePicture.startsWith('//')) {
            profilePicture = `https:${profilePicture}`;
          }
          if (profilePicture && profilePicture.startsWith('/')) {
            profilePicture = `https://sessionize.com${profilePicture}`;
          }

          return {
            id: raw.id,
            fullName,
            profilePicture,
            bio: raw.bio,
            tagline: raw.tagLine
          };
        };

        const speakersMap = new Map<string, ReturnType<typeof speakerFormatter>>(
          json.speakers.map((speaker) => [speaker.id, speakerFormatter(speaker)])
        );

        const roomsMap = new Map<number, string>(json.rooms.map(r => [r.id, r.name]));

        // Find the "Workshop (90 mins)" category ID dynamically
        const workshopCategoryId = json.categories
          ?.find(cat => cat.title === 'Session format')
          ?.items.find(item => item.name === 'Workshop (90 mins)')
          ?.id;

        // Build a map of Topic category id -> name so we can render human-readable chips
        const topicsCategoryItems = json.categories
          ?.find(cat => cat.title === 'Topics')
          ?.items || [];

        const topicsMap = new Map<number, string>(topicsCategoryItems.map(item => [item.id, item.name]));

        // Limit tracks to the main 4 rooms 
        const uniqueTracks = json.rooms.map(r => r.name).slice(0, 4);
        setTracks(uniqueTracks);

        // Process active timeline sessions safely
        const processed: ProcessedSession[] = json.sessions
          .filter(s => s.startsAt && s.endsAt)
          .map((session) => {
            // Check if session is a workshop based on session format category
            const isWorkshop = workshopCategoryId ? session.categoryItems.includes(workshopCategoryId) : false;

            // Map topic category IDs to readable names for rendering as chips
            const topics = (session.categoryItems || [])
              .filter(id => topicsMap.has(id))
              .map(id => topicsMap.get(id)!) ;

            return {
              id: session.id,
              title: session.title,
              description: session.description,
              startsAt: new Date(session.startsAt!),
              endsAt: new Date(session.endsAt!),
              room: session.roomId ? (roomsMap.get(session.roomId) || uniqueTracks[0]) : uniqueTracks[0],
              isServiceSession: session.isServiceSession,
              isWorkshop,
              topics,
              speakers: session.speakers
                .map((speakerId) => speakersMap.get(speakerId))
                .filter((s): s is ProcessedSpeaker => !!s) as ProcessedSpeaker[]
            };
          });

        // Extract and sort all unique baseline start/end time markers
        const uniqueTimes = Array.from(
          new Set([
            ...processed.map(s => s.startsAt.getTime()),
            ...processed.map(s => s.endsAt.getTime())
          ])
        ).sort((a, b) => a - b);

        // Generate coordinates for time segments. Base grid tracks start at row index 2 (row 1 is header)
        const timeBlocks: TimeIncrement[] = [];
        const timestampToGridRowMap = new Map<number, number>();

        for (let i = 0; i < uniqueTimes.length - 1; i++) {
          const startTime = new Date(uniqueTimes[i]);
          const endTime = new Date(uniqueTimes[i + i < uniqueTimes.length ? i + 1 : i]);
          
          const startLabel = startTime.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
          const endLabel = endTime.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
          
          const gridRowIndex = i + 2; 
          timestampToGridRowMap.set(uniqueTimes[i], gridRowIndex);

          timeBlocks.push({
            label: startLabel,
            endLabel: endLabel,
            gridRowIndex
          });
        }
        
        // Final timestamp mapping boundary cap
        timestampToGridRowMap.set(uniqueTimes[uniqueTimes.length - 1], uniqueTimes.length + 1);

        // Map session layout bounds based on calculated timestamps
        const layoutMappedSessions = processed.map((session) => {
          const rowStart = timestampToGridRowMap.get(session.startsAt.getTime()) || 2;
          const rowEnd = timestampToGridRowMap.get(session.endsAt.getTime()) || 3;
          
          return {
            ...session,
            gridRowStart: rowStart,
            gridRowSpan: rowEnd - rowStart
          };
        });

        setTimeLabels(timeBlocks);
        setSessions(layoutMappedSessions);
        setLoading(false);
      })
      .catch((err: Error) => {
        setError(err.message);
        setLoading(false);
      });
  }, []);

  if (loading) return <div className="loading">Building responsive time-grid architecture...</div>;
  if (error) return <div className="error">Error: {error}</div>;

  const openSessionDetail = (session: ProcessedSession) => setSelectedSession(session);
  const closeSessionDetail = () => setSelectedSession(null);
  const handleSessionKeyDown = (event: React.KeyboardEvent<HTMLDivElement>, session: ProcessedSession) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      openSessionDetail(session);
    }
  };

  return (
    <div className="container-fluid">
      <header className="header">
        <h1>GDG London IO Extended 2026</h1>
      </header>

      <div className="schedule-grid">
        
        {/* Row 1 Grid Track Headers */}
        <div className="track-header" style={{ gridColumn: 1 }}>Time</div>
        {tracks.map((trackName, index) => (
          <div key={index} className="track-header" style={{ gridColumn: index + 2 }}>
            {trackName}
          </div>
        ))}

        {/* Persistent Time-Tick Sidebar Tracks */}
        {timeLabels.map((slot, index) => (
          <div 
            key={index} 
            className="time-tick" 
            style={{ gridRow: slot.gridRowIndex, gridColumn: 1 }}
          >
            <div>{slot.label}</div>
            <div className="time-tick-end">{slot.endLabel}</div>
          </div>
        ))}

        {/* Dynamic Multi-span Sessions Renderer */}
        {sessions.map((session) => {
          const trackColumnIndex = tracks.indexOf(session.room);
          const isServiceSession = session.isServiceSession;
        const isPlenum = session.room.toLowerCase().includes('plenum');
          
          // Determine layout mapping positions
          const gridColumnStyle = isServiceSession ? '2 / span 4' : isPlenum ? '2 / span 4' : `${trackColumnIndex + 2}`;
          const sessionInteractionProps = isServiceSession ? {} : {
            role: 'button',
            tabIndex: 0,
            onClick: () => openSessionDetail(session),
            onKeyDown: (event: React.KeyboardEvent<HTMLDivElement>) => handleSessionKeyDown(event, session),
          };

          return (
            <div
              key={session.id}
              {...sessionInteractionProps}
              aria-disabled={isServiceSession}
              className={`session-card ${isServiceSession ? 'service-session-card' : ''} ${isPlenum ? 'plenum-card' : ''} ${session.isWorkshop ? 'workshop-card' : 'talk-card'}`}
              style={{
                gridColumn: gridColumnStyle,
                gridRow: `${session.gridRowStart} / span ${session.gridRowSpan}`
              }}
              data-room={session.room}
            >
              <div className="session-time-mobile">
                {session.startsAt.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                {' '} • {' '}
                {Math.round((session.endsAt.getTime() - session.startsAt.getTime()) / 60000)} min
              </div>
              <div>
                <h2 className="session-title">{session.title}</h2>
                {session.speakers.length > 0 && (
                  <div className="speaker-container speaker-container--inline">
                    {session.speakers.map((speaker) => (
                      <span key={speaker.id} className="speaker-tag speaker-tag--inline">
                        <User size={12} />
                        {speaker.fullName}
                      </span>
                    ))}
                  </div>
                )}
                {session.description && (
                  <p className="session-desc">
                    {session.description.length > 140 
                      ? `${session.description.substring(0, 140)}...` 
                      : session.description}
                  </p>
                )}
              </div>

              {session.topics && session.topics.length > 0 && (
                <div className="session-topics">
                  {session.topics.map((t, i) => (
                    <span key={i} className="topic-chip">{t}</span>
                  ))}
                </div>
              )}
            </div>
          );
        })}

      </div>

      {selectedSession && (
        <div className="modal-backdrop" onClick={closeSessionDetail}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={closeSessionDetail} aria-label="Close session details">
              ×
            </button>
            <div className="modal-header">
              <div>
                <div className="session-meta">
                  <span>{selectedSession.room}</span>
                  <span>
                    {selectedSession.startsAt.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                    {' '}–{' '}
                    {selectedSession.endsAt.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
                {selectedSession.topics && selectedSession.topics.length > 0 && (
                  <div className="session-topics">
                    {selectedSession.topics.map((t, i) => (
                      <span key={i} className="topic-chip">{t}</span>
                    ))}
                  </div>
                )}
                <h2>{selectedSession.title}</h2>
              </div>
            </div>

            <div className="modal-body">
              {selectedSession.description ? (
                <p>{selectedSession.description}</p>
              ) : (
                <p className="session-desc">No description available for this session.</p>
              )}

              {selectedSession.speakers.length > 0 && (
                <div className="modal-speaker-list">
                  {selectedSession.speakers.map((speaker) => (
                    <div key={speaker.id} className="modal-speaker-card">
                      {speaker.profilePicture ? (
                        <img
                          className="speaker-photo"
                          src={speaker.profilePicture}
                          alt={speaker.fullName}
                        />
                      ) : (
                        <div className="speaker-photo speaker-photo--fallback">
                          <User size={28} />
                        </div>
                      )}
                      <div className="speaker-details">
                        <strong>{speaker.fullName}</strong>
                        {speaker.tagline && <p className="speaker-tagline">{speaker.tagline}</p>}
                        {speaker.bio && <p className="speaker-bio">{speaker.bio}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;